import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { ReactQuillWrapperProps, ReactQuillWrapperRef } from './dto';
import { useField } from 'formik';
import { debounce } from 'lodash';
import DOMPurify from 'dompurify';
import styled from '@emotion/styled';
import { Box, Typography } from '@mui/material';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';

const StyledReactQuill = styled(ReactQuill)<{ minHeight: string; hasError?: boolean; readOnly?: boolean }>`
    .ql-container {
        border-color: ${(props) => {
            if (props.hasError) return '#d32f2f';
            if (props.readOnly) return '#e0e0e0';
            return undefined;
        }};
        background-color: ${(props) => (props.readOnly ? '#f5f5f5' : undefined)};
    }
    .ql-toolbar {
        border-color: ${(props) => {
            if (props.hasError) return '#d32f2f';
            if (props.readOnly) return '#e0e0e0';
            return undefined;
        }};
        background-color: ${(props) => (props.readOnly ? '#f5f5f5' : undefined)};
        pointer-events: ${(props) => (props.readOnly ? 'none' : undefined)};
    }
    .ql-editor {
        min-height: ${(props) => props.minHeight};
        cursor: ${(props) => (props.readOnly ? 'not-allowed' : undefined)};
    }
`;

const ReactQuillWrapper = forwardRef<ReactQuillWrapperRef, ReactQuillWrapperProps>(
    ({ name, minHeight = '10px', maxLength, readOnly = false, className, onFocus }, ref) => {
        const quillInstanceRef = useRef<ReactQuill>(null);
        const [field, meta, helpers] = useField({ name });
        const [charCount, setCharCount] = useState(0);

        // Check if field has been touched and has an error
        const hasError = meta.touched && Boolean(meta.error);
        const errorMessage = meta.touched && meta.error ? String(meta.error) : '';

        useImperativeHandle(ref, () => ({
            setHtml: (html: string) => {
                const editor = quillInstanceRef.current?.getEditor();
                if (editor) {
                    editor.clipboard.dangerouslyPasteHTML(html);
                }
            },
        }));

        const getPlainTextLength = (html: string): number => {
            if (!html) return 0;
            const plainText = html.replace(/<[^>]*>/g, '');
            return plainText.trim().length;
        };

        const handleChange = debounce(async (editor: ReactQuill.UnprivilegedEditor) => {
            const html = editor.getSemanticHTML().replaceAll('&nbsp;', ' ');
            const sanitizedHtml = DOMPurify.sanitize(html, {
                ALLOWED_TAGS: ['p', 'h1', 'h2', 'h3', 'strong', 'em', 'u', 'a', 'ol', 'ul', 'li'],
            });

            // Set value and trigger validation (validateOnChange might be true)
            helpers.setValue(sanitizedHtml, true); // true = should validate

            // Mark field as touched when user types (so errors display immediately)
            if (!meta.touched) {
                helpers.setTouched(true, false);
            }

            // Update character count
            if (maxLength) {
                setCharCount(getPlainTextLength(sanitizedHtml));
            }
        }, 1000);

        return (
            <Box>
                <StyledReactQuill
                    ref={quillInstanceRef}
                    theme="snow"
                    defaultValue={field.value}
                    onChange={(_, __, ___, editor) => handleChange(editor)}
                    minHeight={minHeight}
                    hasError={hasError}
                    readOnly={readOnly}
                    modules={{
                        toolbar: readOnly ? false : undefined,
                    }}
                    className={className}
                    onFocus={onFocus}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                    {/* Error message on the left */}
                    {errorMessage && (
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'error.main',
                            }}
                        >
                            {errorMessage}
                        </Typography>
                    )}
                    {/* Character counter on the right */}
                    {maxLength && (
                        <Typography
                            variant="caption"
                            sx={{
                                color: charCount > maxLength ? 'error.main' : 'text.secondary',
                                ml: 'auto', // Push to the right
                            }}
                        >
                            {charCount} / {maxLength}
                        </Typography>
                    )}
                </Box>
            </Box>
        );
    },
);

export default ReactQuillWrapper;
